import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Divider, Grid, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField,
  Typography, Alert, Stack, Tooltip,
} from '@mui/material';
import {
  ArrowBack, CheckCircle, Payments, ReceiptLong, Business, Assignment, AccountBalance,
  Rule, ReplayCircleFilled, WarningAmber, Article, Timeline as TimelineIcon,
} from '@mui/icons-material';
import { procurementService } from '../../services/procurementService';
import { scmService } from '../../services/scmService';
import { StatusDot } from '../../components/common/pageKit';
import AttachmentsPanel from '../../components/common/AttachmentsPanel';

const inr = (n: any) => `₹${new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0)}`;
const fmtDate = (d: any) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const num = (v: any) => Number(v) || 0;

const EXCEPTION_LABEL: Record<string, string> = {
  QTY_OVER_RECEIPT: 'Qty exceeds receipt', QTY_UNDER_RECEIPT: 'Qty under receipt',
  PRICE_VARIANCE: 'Price variance', VALUE_VARIANCE: 'Value variance', TAX_MISMATCH: 'Tax mismatch',
  VENDOR_MISMATCH: 'Vendor mismatch', NO_PO: 'No PO linked', NO_GRN: 'No goods receipt',
  DUPLICATE_INVOICE: 'Possible duplicate',
};
const MATCH_TONE: Record<string, any> = { MATCHED: 'success', WITHIN_TOLERANCE: 'warning', OVERRIDDEN: 'warning', BLOCKED: 'error', UNMATCHED: 'neutral' };

const SectionCard: React.FC<{ title: string; icon: React.ReactNode; action?: React.ReactNode; children: React.ReactNode }> = ({ title, icon, action, children }) => (
  <Paper variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
    <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
      {icon}
      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{title}</Typography>
      <Box sx={{ flexGrow: 1 }} />
      {action}
    </Box>
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Paper>
);

const KV: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Grid size={{ xs: 6, sm: 4, md: 3 }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
  </Grid>
);

const PurchaseInvoiceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [inv, setInv] = useState<any | null>(null);
  const [exceptions, setExceptions] = useState<any[]>([]);
  const [matchStatus, setMatchStatus] = useState<string>('UNMATCHED');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ovr, setOvr] = useState<{ exc: any; reason: string } | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [{ invoice }, exc] = await Promise.all([
        procurementService.getPurchaseInvoice(id) as any,
        scmService.getMatchExceptions({ invoiceId: id } as any).catch(() => ({ exceptions: [] as any[] })),
      ]);
      setInv(invoice);
      const rows = (exc?.exceptions || []) as any[];
      setExceptions(rows);
      setMatchStatus(rows[0]?.match_status || invoice?.match_status || 'UNMATCHED');
      setError(null);
    } catch (e: any) {
      setError(e.message || 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const runMatch = async () => {
    if (!id) return;
    setBusy(true);
    try { await scmService.evaluateMatch(id); await load(); } catch (e: any) { setError(e.message || 'Match failed'); } finally { setBusy(false); }
  };
  const approve = async () => {
    if (!id) return;
    setBusy(true);
    try { await procurementService.approvePurchaseInvoice(id); await load(); } catch (e: any) { setError(e.message || 'Approve failed'); } finally { setBusy(false); }
  };
  const doOverride = async () => {
    if (!ovr || !ovr.reason.trim()) return;
    setBusy(true);
    try { await scmService.overrideMatchException(ovr.exc.id, ovr.reason.trim()); setOvr(null); await load(); }
    catch (e: any) { setError(e.message || 'Override failed'); } finally { setBusy(false); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;
  if (error && !inv) return <Box sx={{ p: 3 }}><Alert severity="error" action={<Button onClick={load}>Retry</Button>}>{error}</Alert></Box>;
  if (!inv) return null;

  const total = num(inv.net_amount);
  const paid = num(inv.paid_amount);
  const outstanding = Math.max(0, total - paid);
  const isPending = inv.status === 'PENDING_APPROVAL' || inv.status === 'PENDING';
  const posted = !isPending; // posts to GL on approval
  const overdueDays = inv.due_date && outstanding > 0 ? Math.floor((Date.now() - new Date(inv.due_date).getTime()) / 86400000) : 0;
  const open = exceptions.filter((e) => e.status === 'OPEN');
  const blocking = open.filter((e) => e.severity === 'BLOCKING');

  return (
    <Box sx={{ p: 3 }}>
      <Button size="small" startIcon={<ArrowBack />} onClick={() => navigate('/procurement/invoices')} sx={{ mb: 1.5 }}>Invoices</Button>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Header band */}
      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 20 }}>{inv.invoice_no}</Typography>
            <Typography variant="body2" color="text.secondary">{inv.vendor_name}{inv.vendor_invoice_number ? ` · Vendor Inv: ${inv.vendor_invoice_number}` : ''}</Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">Total</Typography>
            <Typography sx={{ fontWeight: 800, fontSize: 18 }}>{inr(total)}</Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">Outstanding</Typography>
            <Typography sx={{ fontWeight: 800, fontSize: 18, color: outstanding > 0 ? 'warning.main' : 'success.main' }}>{inr(outstanding)}</Typography>
          </Box>
          <Stack spacing={0.75} alignItems="flex-end">
            <StatusDot label={String(inv.status || '').replace(/_/g, ' ')} tone={isPending ? 'warning' : (inv.payment_status === 'PAID' ? 'success' : 'info')} />
            <StatusDot label={`Match: ${matchStatus.replace(/_/g, ' ')}`} tone={MATCH_TONE[matchStatus] || 'neutral'} />
          </Stack>
        </Box>
        <Divider sx={{ my: 1.5 }} />
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {isPending && (
            <Tooltip title={blocking.length ? 'Has blocking match exceptions — override them first' : ''}>
              <span><Button variant="contained" size="small" startIcon={<CheckCircle />} disabled={busy} onClick={approve}>Approve &amp; Post</Button></span>
            </Tooltip>
          )}
          <Button variant="outlined" size="small" startIcon={<Payments />} onClick={() => navigate('/procurement/payments/new')}>Record Payment</Button>
          {inv.po_id && <Button variant="text" size="small" startIcon={<Assignment />} onClick={() => navigate(`/procurement/orders/${inv.po_id}`)}>View PO {inv.po_number || ''}</Button>}
        </Stack>
      </Paper>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          {/* Summary */}
          <SectionCard title="Summary" icon={<Business fontSize="small" color="primary" />}>
            <Grid container spacing={2}>
              <KV label="Vendor" value={inv.vendor_name} />
              <KV label="Vendor GSTIN" value={inv.vendor_gstin || '—'} />
              <KV label="PO" value={inv.po_number || '—'} />
              <KV label="Invoice Date" value={fmtDate(inv.invoice_date)} />
              <KV label="Due Date" value={<>{fmtDate(inv.due_date)} {overdueDays > 0 && <Chip size="small" color="error" label={`Overdue ${overdueDays}d`} sx={{ height: 18, fontSize: 10, ml: 0.5 }} />}</>} />
              <KV label="Payment Status" value={String(inv.payment_status || '—')} />
            </Grid>
          </SectionCard>

          {/* Line items */}
          <SectionCard title="Line Items" icon={<ReceiptLong fontSize="small" color="primary" />}>
            <Table size="small">
              <TableHead>
                <TableRow>{['Product', 'HSN', 'Qty', 'Rate', 'GST', 'Amount'].map((h) => <TableCell key={h} align={['Qty', 'Rate', 'GST', 'Amount'].includes(h) ? 'right' : 'left'} sx={{ fontWeight: 700, fontSize: 12 }}>{h}</TableCell>)}</TableRow>
              </TableHead>
              <TableBody>
                {(inv.items || []).length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 3 }}>Header-only bill (no line items)</TableCell></TableRow>}
                {(inv.items || []).map((it: any) => (
                  <TableRow key={it.id}>
                    <TableCell>
                      <Typography variant="body2" onClick={it.product_id ? () => navigate(`/entity/material/${it.product_id}`) : undefined} sx={{ ...(it.product_id ? { color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } } : {}) }}>
                        {it.product_name || `Product #${it.product_id}`}
                      </Typography>
                    </TableCell>
                    <TableCell>{it.hsn_code || '—'}</TableCell>
                    <TableCell align="right">{num(it.quantity)} {it.unit || ''}</TableCell>
                    <TableCell align="right">{inr(it.unit_price)}</TableCell>
                    <TableCell align="right">{inr(num(it.cgst_amount) + num(it.sgst_amount) + num(it.igst_amount))}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{inr(it.line_total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </SectionCard>

          {/* 3-way match */}
          <SectionCard title="3-Way Match" icon={<Rule fontSize="small" color="primary" />}
            action={<Button size="small" startIcon={<ReplayCircleFilled />} disabled={busy} onClick={runMatch}>Run Match</Button>}>
            <Box sx={{ mb: 1.5 }}><StatusDot label={matchStatus.replace(/_/g, ' ')} tone={MATCH_TONE[matchStatus] || 'neutral'} /></Box>
            {open.length === 0 ? (
              <Alert severity={matchStatus === 'MATCHED' ? 'success' : 'info'} icon={<CheckCircle fontSize="inherit" />}>
                {matchStatus === 'MATCHED' ? 'PO, receipt and invoice match with no open exceptions.' : 'No open match exceptions. Run match to (re)evaluate against PO & GRN.'}
              </Alert>
            ) : (
              <Table size="small">
                <TableHead><TableRow>{['Check', 'Severity', 'Expected', 'Actual', 'Variance', ''].map((h) => <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12 }}>{h}</TableCell>)}</TableRow></TableHead>
                <TableBody>
                  {open.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell>{EXCEPTION_LABEL[e.exception_type] || e.exception_type}<Typography variant="caption" display="block" color="text.secondary">{e.detail}</Typography></TableCell>
                      <TableCell><Chip size="small" color={e.severity === 'BLOCKING' ? 'error' : 'warning'} label={e.severity} sx={{ height: 20 }} /></TableCell>
                      <TableCell>{e.expected_value ?? '—'}</TableCell>
                      <TableCell>{e.actual_value ?? '—'}</TableCell>
                      <TableCell>{e.variance != null ? `${e.variance}${e.variance_pct != null ? ` (${e.variance_pct}%)` : ''}` : '—'}</TableCell>
                      <TableCell><Button size="small" startIcon={<WarningAmber />} onClick={() => setOvr({ exc: e, reason: '' })}>Override</Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          {/* Tax */}
          <SectionCard title="Tax" icon={<ReceiptLong fontSize="small" color="primary" />}>
            <Grid container spacing={1.5}>
              <KV label="Taxable" value={inr(inv.gross_amount)} />
              <KV label="CGST" value={inr(inv.cgst_amount)} />
              <KV label="SGST" value={inr(inv.sgst_amount)} />
              <KV label="IGST" value={inr(inv.igst_amount)} />
              <KV label="Total GST" value={inr(inv.gst_amount)} />
              <KV label="Total" value={inr(total)} />
            </Grid>
          </SectionCard>

          {/* Accounting */}
          <SectionCard title="Accounting" icon={<AccountBalance fontSize="small" color="primary" />}>
            {posted ? (
              <Alert severity="success" icon={<CheckCircle fontSize="inherit" />}>
                Posted to the ledger — Dr Purchases/Inventory + Input GST, Cr Accounts Payable.
                <Button size="small" sx={{ ml: 1 }} onClick={() => navigate('/finance/journal')}>View Journal</Button>
              </Alert>
            ) : (
              <Alert severity="info">Not yet posted. The bill posts to the general ledger when it is approved.</Alert>
            )}
          </SectionCard>

          {/* Payment */}
          <SectionCard title="Payment" icon={<Payments fontSize="small" color="primary" />}>
            <Grid container spacing={1.5}>
              <KV label="Total" value={inr(total)} />
              <KV label="Paid" value={inr(paid)} />
              <KV label="Outstanding" value={inr(outstanding)} />
              <KV label="Status" value={String(inv.payment_status || '—')} />
            </Grid>
            {outstanding > 0 && (
              <Button variant="outlined" size="small" startIcon={<Payments />} sx={{ mt: 1.5 }} onClick={() => navigate('/procurement/payments/new')}>Record Payment</Button>
            )}
          </SectionCard>

          <Box sx={{ mb: 2 }}>
            <AttachmentsPanel entityType="purchase_invoice" entityId={inv.id} title="Invoice Documents" />
          </Box>

          {/* Lifecycle */}
          <SectionCard title="Lifecycle" icon={<TimelineIcon fontSize="small" color="primary" />}>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} alignItems="center"><CheckCircle fontSize="small" color="success" /><Box><Typography variant="body2" sx={{ fontWeight: 600 }}>Created</Typography><Typography variant="caption" color="text.secondary">{fmtDate(inv.created_at)}</Typography></Box></Stack>
              <Stack direction="row" spacing={1} alignItems="center">{posted ? <CheckCircle fontSize="small" color="success" /> : <Article fontSize="small" color="disabled" />}<Box><Typography variant="body2" sx={{ fontWeight: 600 }}>Approved &amp; Posted</Typography><Typography variant="caption" color="text.secondary">{posted ? 'Posted to GL' : 'Pending approval'}</Typography></Box></Stack>
              <Stack direction="row" spacing={1} alignItems="center">{inv.payment_status === 'PAID' ? <CheckCircle fontSize="small" color="success" /> : <Payments fontSize="small" color="disabled" />}<Box><Typography variant="body2" sx={{ fontWeight: 600 }}>Paid</Typography><Typography variant="caption" color="text.secondary">{inv.payment_status === 'PAID' ? 'Settled' : `${inr(outstanding)} outstanding`}</Typography></Box></Stack>
            </Stack>
          </SectionCard>
        </Grid>
      </Grid>

      {/* Override dialog */}
      <Dialog open={!!ovr} onClose={() => setOvr(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Override match exception</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {ovr && (EXCEPTION_LABEL[ovr.exc.exception_type] || ovr.exc.exception_type)} — {ovr?.exc.detail}. Overriding is an audited financial control; a reason is required.
          </DialogContentText>
          <TextField fullWidth multiline minRows={2} label="Reason for override (required)" value={ovr?.reason || ''} onChange={(e) => setOvr((p) => (p ? { ...p, reason: e.target.value } : p))} autoFocus />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOvr(null)}>Cancel</Button>
          <Button variant="contained" disabled={busy || !ovr?.reason.trim()} onClick={doOverride}>Override</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PurchaseInvoiceDetailPage;
